import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, ArrowRight } from "lucide-react";
import Select from "react-select";
import axiosClient from "../../api/axiosClient";
import { login } from "./services/authService";
import { useAuth } from "../../app/providers/AuthProvider";
import {
  COUNTRIES,
  INDIA_STATES_AND_UTS,
  INDUSTRIES,
} from "../../constants/signupOptions";

const SIGNUP_OTP_CONFIG_TOKEN =
  process.env.REACT_APP_SIGNUP_OTP_CONFIG_TOKEN ||
  process.env.VITE_SIGNUP_OTP_CONFIG_TOKEN ||
  "";
const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  process.env.VITE_GOOGLE_CLIENT_ID ||
  "";
const GOOGLE_REDIRECT_URI =
  process.env.REACT_APP_GOOGLE_REDIRECT_URI ||
  process.env.VITE_GOOGLE_REDIRECT_URI ||
  "";
const GOOGLE_SIGNUP_STATE_KEY = "xb_signup_google_oauth_state";

  const SignupForTrial = () => {
    const navigate = useNavigate();
  const { refreshAuthContext } = useAuth();
  const [isGoogleSignup, setIsGoogleSignup] = useState(false);
  const requiresPassword = true;

  const [formData, setFormData] = useState({
    mobileNumber: "",
    email: "",
    password: "",
  });

  const [profileData, setProfileData] = useState({
    fullName: "",
    companyName: "",
    companyDescription: "",
    country: "",
    state: "",
    companySize: "",
    industry: "",
    website: "",
  });

  const [authMethod, setAuthMethod] = useState(null); // null = choice screen, 'email' = email form
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [verificationToken, setVerificationToken] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const otpInputRefs = useRef([]);

  const TESTIMONIALS = [
    {
      quote: "Best decision we made for our customer operations this year. The AI handles most of our inbound while our team focuses on closing.",
      name: "Rahul Sharma",
      role: "Head of Growth · TechVentures India",
      initials: "RS",
      gradient: "from-emerald-500 to-teal-600",
    },
    {
      quote: "We cut our average response time from 4 hours to under 2 minutes. Our CSAT scores jumped 35% in the first month alone.",
      name: "Priya Mehta",
      role: "Customer Success Lead · Freshloop",
      initials: "PM",
      gradient: "from-teal-500 to-cyan-600",
    },
    {
      quote: "Finally a platform that doesn't treat WhatsApp as an afterthought. Broadcasting, automation, analytics — all in one place.",
      name: "Arjun Nair",
      role: "Co-founder · ShipFast Commerce",
      initials: "AN",
      gradient: "from-emerald-600 to-green-500",
    },
    {
      quote: "Our sales team went from juggling 5 tools to just XploreByte. Pipeline visibility improved overnight.",
      name: "Sneha Iyer",
      role: "VP Sales · CloudBridge Solutions",
      initials: "SI",
      gradient: "from-cyan-500 to-emerald-500",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [TESTIMONIALS.length]);

  const toUserFriendlyOtpError = rawMessage => {
    const text = String(rawMessage || "").trim();
    const normalized = text.toUpperCase();

    if (
      normalized.includes("INVALID_OTP_CONFIG_TOKEN") ||
      normalized.includes("OTP_CONFIG_TOKEN") ||
      normalized.includes("META_CLOUD_SETTINGS_NOT_FOUND") ||
      normalized.includes("META_CLOUD") ||
      normalized.includes("OTP_TEMPLATE_NOT_FOUND") ||
      normalized.includes("OTP_TEMPLATE_NOT_CONFIGURED") ||
      normalized.includes("INVALID_SENDER_PHONE_NUMBER_ID") ||
      normalized.includes("OTP_SEND_FAILED")
    ) {
      return "OTP could not be sent due to a technical issue. Please wait a moment and try again.";
    }

    if (
      normalized.includes("OTP_HOURLY_LIMIT_REACHED") ||
      (normalized.includes("OTP") &&
        normalized.includes("HOURLY") &&
        normalized.includes("LIMIT"))
    ) {
      return "You have exceeded your OTP send limit. Please try again after 1 hour.";
    }

    if (
      normalized.includes("OTP_DAILY_LIMIT_REACHED") ||
      (normalized.includes("OTP") &&
        normalized.includes("DAILY") &&
        normalized.includes("LIMIT"))
    ) {
      return "You have exceeded your OTP send limit for today. Please try again tomorrow.";
    }

    return text || "Failed to send OTP.";
  };

  const handleInputChange = e => {
    const { name, value } = e.target;

    if (name === "mobileNumber") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [name]: digitsOnly,
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileInputChange = e => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value,
      ...(name === "country" ? { state: "" } : {}),
    }));
  };

  const verifyGoogleCredential = useCallback(async credential => {
    const response = await axiosClient.post(
      "/auth/google/verify",
      {
        credential,
      },
      { __silentToast: true },
    );
    const isSuccess =
      response?.data?.success ?? response?.data?.Success ?? false;
    if (!isSuccess) {
      throw new Error(response?.data?.message || "Google verification failed.");
    }

    const payload =
      response?.data?.payload ||
      response?.data?.Payload ||
      response?.data?.data ||
      response?.data?.Data ||
      {};

    const email = payload?.email || payload?.Email || "";
    const fullName = payload?.fullName || payload?.FullName || "";
    if (!email) {
      throw new Error("Google identity claims are missing.");
    }

    setFormData(prev => ({
      ...prev,
      email,
      password: "",
    }));
    setProfileData(prev => ({
      ...prev,
      fullName: fullName || prev.fullName,
    }));
    setIsGoogleSignup(true);
    setShowPassword(false);
  }, []);

  const getGoogleRedirectUri = () => {
    const configured = String(GOOGLE_REDIRECT_URI || "").trim();
    if (configured) return configured;
    return `${window.location.origin}/signup-for-trial`;
  };

  const clearGoogleOAuthArtifactsFromUrl = () => {
    const cleanPath = `${window.location.pathname}${window.location.search}`;
    window.history.replaceState({}, document.title, cleanPath);
  };

  const handleContinueWithGoogle = async () => {
    setError("");

    if (!GOOGLE_CLIENT_ID) {
      setError("Google Client ID missing. Set REACT_APP_GOOGLE_CLIENT_ID.");
      return;
    }

    if (googleLoading) return;

    setGoogleLoading(true);
    try {
      const redirectUri = getGoogleRedirectUri();
      const state = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(GOOGLE_SIGNUP_STATE_KEY, state);

      const authUrl = new URL("https://accounts.google.com/o/oauth2/v2/auth");
      authUrl.searchParams.set("client_id", GOOGLE_CLIENT_ID);
      authUrl.searchParams.set("redirect_uri", redirectUri);
      authUrl.searchParams.set("response_type", "id_token");
      authUrl.searchParams.set("scope", "openid email profile");
      authUrl.searchParams.set("prompt", "select_account");
      authUrl.searchParams.set("state", state);
      authUrl.searchParams.set("nonce", state);

      window.location.assign(authUrl.toString());
      return;
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Google sign-in failed.";
      setError(message);
    } finally {
      setGoogleLoading(false);
    }
  };

  useEffect(() => {
    const handleGoogleRedirectCallback = async () => {
      const hash = String(window.location.hash || "").replace(/^#/, "");
      const hashParams = new URLSearchParams(hash);
      const queryParams = new URLSearchParams(window.location.search || "");

      const idToken =
        hashParams.get("id_token") ||
        queryParams.get("id_token") ||
        "";
      const oauthError =
        hashParams.get("error") ||
        queryParams.get("error") ||
        "";
      const returnedState =
        hashParams.get("state") ||
        queryParams.get("state") ||
        "";

      if (!idToken && !oauthError) return;

      clearGoogleOAuthArtifactsFromUrl();

      if (oauthError) {
        setError(`Google sign-in failed (${oauthError}). Please try again.`);
        return;
      }

      const storedState = sessionStorage.getItem(GOOGLE_SIGNUP_STATE_KEY) || "";
      sessionStorage.removeItem(GOOGLE_SIGNUP_STATE_KEY);

      if (!returnedState || !storedState || returnedState !== storedState) {
        setError("Google sign-in validation failed. Please try again.");
        return;
      }

      setGoogleLoading(true);
      try {
        await verifyGoogleCredential(idToken);
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Google sign-in failed.";
        setError(message);
      } finally {
        setGoogleLoading(false);
      }
    };

    handleGoogleRedirectCallback();
  }, [verifyGoogleCredential]);

  const checkSignupEmailAvailability = async email => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail) {
      setError("Email is required.");
      return false;
    }

    try {
      const response = await axiosClient.post(
        "/auth/signup-email/check",
        { email: normalizedEmail },
        { __silentToast: true },
      );

      const isSuccess =
        response?.data?.success ?? response?.data?.Success ?? false;

      if (!isSuccess) {
        throw new Error(response?.data?.message || "Email check failed.");
      }

      return true;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Email check failed.";
      setError(message);
      return false;
    }
  };

  const sendOtp = async phone10Digits => {
    setError("");
    const normalizedPhone = (phone10Digits || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    const configToken = String(SIGNUP_OTP_CONFIG_TOKEN || "").trim();

    if (normalizedPhone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return null;
    }

    if (!configToken) {
      setError(
        "OTP config token is missing. Set REACT_APP_SIGNUP_OTP_CONFIG_TOKEN in frontend env.",
      );
      return null;
    }

    if (resendCooldown > 0) {
      setError(
        `Please wait ${resendCooldown}s before requesting OTP again.`,
      );
      return null;
    }

    if (otpLoading) return null;

    setOtpLoading(true);
    try {
      const response = await axiosClient.post(
        "/auth/signup-phone-otp/send",
        {
          phone: normalizedPhone,
          configToken,
        },
        { __silentToast: true },
      );

      const isSuccess =
        response?.data?.success ?? response?.data?.Success ?? false;

      if (!isSuccess) {
        throw new Error(response?.data?.message || "Failed to send OTP.");
      }

      setOtpSent(true);
      setOtpVerified(false);
      setVerificationToken("");
      setOtp("");
      setResendCooldown(60);

      return response?.data;
    } catch (err) {
      const message = toUserFriendlyOtpError(
        err?.response?.data?.message || err?.message || "Failed to send OTP.",
      );
      setError(message);
      return null;
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = async (phone10Digits, otpCode) => {
    setError("");
    const normalizedPhone = (phone10Digits || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    const normalizedOtp = (otpCode || "").replace(/\D/g, "").slice(0, 6);
    const configToken = String(SIGNUP_OTP_CONFIG_TOKEN || "").trim();

    if (normalizedPhone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return null;
    }

    if (!configToken) {
      setError(
        "OTP config token is missing. Set REACT_APP_SIGNUP_OTP_CONFIG_TOKEN in frontend env.",
      );
      return null;
    }

    if (normalizedOtp.length !== 6) {
      setError("Please enter a valid 6-digit OTP.");
      return null;
    }

    if (!otpSent) {
      setError("Please request OTP first.");
      return null;
    }

    if (otpLoading) return null;

    if (otpVerified && verificationToken && otp === normalizedOtp) {
      return verificationToken;
    }

    setOtp(normalizedOtp);
    setOtpLoading(true);
    try {
      const response = await axiosClient.post(
        "/auth/signup-phone-otp/verify",
        {
          phone: normalizedPhone,
          otp: normalizedOtp,
          configToken,
        },
        { __silentToast: true },
      );

      const isSuccess =
        response?.data?.success ?? response?.data?.Success ?? false;

      if (!isSuccess) {
        throw new Error(response?.data?.message || "Failed to verify OTP.");
      }

      const payload =
        response?.data?.payload ||
        response?.data?.Payload ||
        response?.data?.data ||
        response?.data?.Data ||
        {};

      const token =
        payload?.verificationToken || payload?.VerificationToken || "";

      if (!token) {
        throw new Error("Verification token not found in response.");
      }

      setVerificationToken(token);
      setOtpVerified(true);

      return token;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to verify OTP.";
      setError(message);
      return null;
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpDigitChange = (index, rawValue) => {
    const value = String(rawValue || "").replace(/\D/g, "");
    if (!value) {
      setOtp(prev => {
        const chars = prev.padEnd(6, " ").slice(0, 6).split("");
        chars[index] = " ";
        return chars.join("").replace(/\s/g, "").slice(0, 6);
      });
      return;
    }

    const digit = value[value.length - 1];
    setOtp(prev => {
      const chars = prev.padEnd(6, " ").slice(0, 6).split("");
      chars[index] = digit;
      return chars.join("").replace(/\s/g, "").slice(0, 6);
    });

    if (index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const current = otp[index] || "";
      if (!current && index > 0) {
        otpInputRefs.current[index - 1]?.focus();
      }
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      otpInputRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = e => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    setOtp(pasted);
    const focusIndex = Math.min(pasted.length, 5);
    otpInputRefs.current[focusIndex]?.focus();
  };

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleStep1Continue = async () => {
    setError("");

    if (
      !profileData.fullName ||
      !formData.email ||
      (requiresPassword && !formData.password)
    ) {
      setError(
        requiresPassword
          ? "Please fill your full name, email and password to continue."
          : "Please fill your full name and email to continue.",
      );
      return;
    }

    if (requiresPassword && formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (requiresPassword) {
      const canUseEmail = await checkSignupEmailAvailability(formData.email);
      if (!canUseEmail) return;
    }

    setCurrentStep(2);
  };

  const handleGoogleStep1Back = () => {
    setError("");
    setIsGoogleSignup(false);
    setShowPassword(false);
    setAuthMethod(null);
    setFormData(prev => ({ ...prev, email: "", password: "" }));
    setProfileData(prev => ({ ...prev, fullName: "" }));
    setCurrentStep(1);
  };

  const handleVerifyOtpAndContinue = async () => {
    setError("");

    if (formData.mobileNumber.length !== 10) {
      setError("Mobile number must be exactly 10 digits");
      return;
    }

    const token = await verifyOtp(formData.mobileNumber, otp);
    if (token) {
      setCurrentStep(3);
    }
  };

  const handleStep2Continue = () => {
    setError("");
    if (!otpVerified || !verificationToken) {
      setError("Please verify your mobile number via OTP to continue.");
      return;
    }
    setCurrentStep(3);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (requiresPassword && formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      setLoading(false);
      return;
    }

    if (formData.mobileNumber.length !== 10) {
      setError("Mobile number must be exactly 10 digits");
      setLoading(false);
      return;
    }

    if (!otpVerified || !verificationToken) {
      setError("Please verify your mobile number via OTP before signing up.");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        companyName: profileData.companyName || "",
        phone: formData.mobileNumber,
        email: formData.email,
        password: formData.password,
        signupPhoneVerificationToken: verificationToken,
        fullName: profileData.fullName || "",
        companyDescription: profileData.companyDescription || "",
        country: profileData.country || "",
        state: profileData.state || "",
        companySize: profileData.companySize || "",
        industry: profileData.industry || "",
        website: profileData.website || "",
      };

      const response = await axiosClient.post(
        "/auth/business-user-signup",
        payload,
      );

      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Signup failed. Try again.");
      }

      // Auto-login immediately after successful 3-step signup.
      await login(formData.email, formData.password);
      await refreshAuthContext();
      navigate("/app/welcomepage", { replace: true });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Signup failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    profileData.fullName &&
    profileData.companyName &&
    formData.mobileNumber.length === 10 &&
    formData.email &&
    (!requiresPassword || formData.password) &&
    otpVerified &&
    verificationToken &&
    acceptTerms;

  const isStep1Ready =
    String(profileData.fullName || "").trim().length > 0 &&
    String(formData.email || "").trim().length > 0 &&
    String(formData.password || "").trim().length > 0;

  const steps = [
    { id: 1, title: "Basic Details" },
    { id: 2, title: "OTP Verification" },
    { id: 3, title: "Profile" },
  ];

  const isIndiaSelected = profileData.country === "India";
  const countryOptions = COUNTRIES.map(country => ({
    value: country,
    label: country,
  }));
  const stateOptions = INDIA_STATES_AND_UTS.map(state => ({
    value: state,
    label: state,
  }));
  const industryOptions = INDUSTRIES.map(industry => ({
    value: industry,
    label: industry,
  }));
  const signupSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 36,
      height: 36,
      borderRadius: 10,
      borderColor: state.isFocused ? "#10b981" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(16,185,129,0.12)" : "none",
      "&:hover": { borderColor: state.isFocused ? "#10b981" : "#9ca3af" },
    }),
    valueContainer: base => ({ ...base, padding: "0 10px" }),
    input: base => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: base => ({ ...base, height: 36 }),
    placeholder: base => ({ ...base, color: "#9ca3af" }),
    singleValue: base => ({ ...base, color: "#111827" }),
    clearIndicator: base => ({
      ...base,
      color: "#6b7280",
      ":hover": { color: "#111827" },
    }),
    dropdownIndicator: base => ({
      ...base,
      color: "#6b7280",
      ":hover": { color: "#111827" },
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#0f766e"
        : state.isFocused
          ? "rgba(16,185,129,0.10)"
          : "white",
      color: state.isSelected ? "white" : "#111827",
      ":active": {
        ...base[":active"],
        backgroundColor: state.isSelected ? "#115e59" : "rgba(16,185,129,0.18)",
      },
    }),
    menu: base => ({ ...base, zIndex: 30 }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="flex flex-col lg:flex-row w-full max-w-6xl min-h-[620px] shadow-2xl rounded-2xl overflow-hidden bg-white">
        {/* ── Left panel ── */}
        <div className="lg:w-1/2 relative bg-[#f4f5f3] p-8 lg:p-10 flex flex-col justify-between overflow-hidden min-h-[620px]">
          {/* Background layers */}
          <div className="pointer-events-none absolute inset-0 z-0">
            {/* Top-right emerald wash */}
            <div className="absolute -top-20 -right-16 w-80 h-80 rounded-full bg-emerald-300 blur-[90px]" />
            {/* Bottom-left teal wash */}
            <div className="absolute -bottom-24 -left-16 w-96 h-96 rounded-full bg-teal-200/90 blur-[100px]" />
            {/* Center soft emerald */}
            <div className="absolute top-1/2 left-1/3 w-60 h-60 rounded-full bg-emerald-200/85 blur-[80px] -translate-y-1/2" />
            {/* Subtle dot grid */}
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage: "radial-gradient(circle, rgba(15,23,20,0.6) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
            {/* Edge fade */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,#f4f5f3_95%)]" />
          </div>

          {/* Logo */}
          <div className="relative z-10 flex items-center gap-2">
            <img
              src="/logo/xplorebyte-icon.svg"
              alt="XploreByte"
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-semibold text-gray-900 tracking-tight">
              XploreByte
            </span>
          </div>

          {/* Center content */}
          <div className="relative z-10 flex flex-col gap-9">

            {/* Headline */}
            <div>
              <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-[0.3em] mb-4">
                WhatsApp Business Platform
              </p>
              <h2
                className="font-extrabold text-gray-900 leading-[1.08] tracking-tight"
                style={{ fontSize: "clamp(1.75rem, 2.9vw, 2.35rem)" }}
              >
                Built for businesses
                <br />
                that take{" "}
                <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
                  WhatsApp
                </span>
                <br />
                seriously.
              </h2>
            </div>

            {/* Auto-scrolling testimonials */}
            <div className="relative" style={{ minHeight: "140px" }}>
              <svg
                className="absolute -top-2 -left-1 w-9 h-9 text-emerald-600/20"
                viewBox="0 0 32 32"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M10 8H4v8c0 4 2 6 6 6v-2c-2 0-4-1-4-4h4V8zm14 0h-6v8c0 4 2 6 6 6v-2c-2 0-4-1-4-4h4V8z" />
              </svg>
              {TESTIMONIALS.map((t, i) => (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-700 ease-in-out"
                  style={{
                    opacity: activeTestimonial === i ? 1 : 0,
                    transform: activeTestimonial === i ? "translateY(0)" : "translateY(8px)",
                    pointerEvents: activeTestimonial === i ? "auto" : "none",
                  }}
                >
                  <p
                    className="text-gray-700 leading-relaxed pl-9"
                    style={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: "15px",
                      lineHeight: 1.6,
                    }}
                  >
                    {t.quote}
                  </p>
                  <div className="flex items-center gap-3 mt-5 pl-9">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-gray-900 text-[12px] font-semibold leading-none">
                        {t.name}
                      </p>
                      <p className="text-gray-500 text-[10.5px] mt-1">
                        {t.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {/* Dot indicators */}
              <div className="absolute bottom-0 left-9 flex items-center gap-1.5">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveTestimonial(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      activeTestimonial === i
                        ? "bg-emerald-600 w-4"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Inline metrics */}
            <div className="flex items-center gap-5 pt-5 border-t border-gray-200">
              {[
                { value: "2.4M+", label: "Messages sent" },
                { value: "98%", label: "Delivery rate" },
                { value: "300+", label: "Active teams" },
              ].map(({ value, label }, i) => (
                <div key={label} className="flex items-center gap-5">
                  <div>
                    <p className="text-gray-900 font-bold text-[15px] leading-none">{value}</p>
                    <p className="text-gray-500 text-[10.5px] mt-1.5">{label}</p>
                  </div>
                  {i < 2 && <div className="w-px h-9 bg-gray-200" />}
                </div>
              ))}
            </div>

          </div>

          {/* Bottom — trust strip */}
          <div className="relative z-10">
            <p className="text-gray-500 text-[12px] leading-snug">
              Trusted by <span className="text-gray-800 font-semibold">300+ businesses</span> across 12 countries
            </p>
          </div>
        </div>

        <div className="lg:w-1/2 relative bg-white px-8 py-8 flex flex-col justify-center min-h-[620px] overflow-hidden border-l border-gray-200">

          {/* ── Method choice screen ── */}
          {currentStep === 1 && authMethod === null && !isGoogleSignup ? (
            <div className="flex flex-col max-w-sm mx-auto w-full">
              <div className="flex justify-end mb-8">
                <Link
                  to="/login"
                  className="text-sm text-gray-500 hover:text-emerald-600 transition"
                >
                  Already a member?{" "}
                  <span className="text-emerald-600 font-medium">Log in</span>
                </Link>
              </div>

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-1.5">
                  Create your account
                </h2>
                <p className="text-sm text-gray-400">
                  14-day free trial &nbsp;·&nbsp; No credit card required
                </p>
              </div>

              <div className="space-y-3">
                {/* Google */}
                <button
                  type="button"
                  onClick={handleContinueWithGoogle}
                  disabled={googleLoading}
                  className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                    googleLoading
                      ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 shadow-sm"
                  }`}
                >
                  <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M21.8 12.2c0-.72-.06-1.26-.19-1.82H12v3.44h5.64c-.11.86-.7 2.16-2 3.03l-.02.11 2.78 2.15.19.02c1.74-1.6 2.74-3.95 2.74-6.93Z" fill="#4285F4"/>
                    <path d="M12 22c2.76 0 5.08-.91 6.77-2.47l-3.23-2.5c-.86.6-2.01 1.02-3.54 1.02-2.7 0-4.99-1.77-5.81-4.22l-.1.01-2.89 2.24-.04.09A10.23 10.23 0 0 0 12 22Z" fill="#34A853"/>
                    <path d="M6.19 13.83A6.14 6.14 0 0 1 5.86 12c0-.64.12-1.26.32-1.83l-.01-.12-2.92-2.28-.1.05A10.08 10.08 0 0 0 2 12c0 1.62.39 3.14 1.08 4.48l3.11-2.65Z" fill="#FBBC05"/>
                    <path d="M12 5.95c1.92 0 3.22.83 3.96 1.52l2.89-2.82C17.07 2.99 14.76 2 12 2a10.23 10.23 0 0 0-8.84 4.82l3.03 2.35c.83-2.45 3.12-4.22 5.81-4.22Z" fill="#EA4335"/>
                  </svg>
                  {googleLoading ? "Connecting…" : "Continue with Google"}
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-gray-100" />
                  <span className="text-xs text-gray-400">or</span>
                  <div className="h-px flex-1 bg-gray-100" />
                </div>

                {/* Email */}
                <button
                  type="button"
                  onClick={() => setAuthMethod("email")}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:border-gray-300 hover:bg-gray-50 shadow-sm transition-all"
                >
                  <svg className="h-4 w-4 shrink-0 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2"/>
                    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                  </svg>
                  Continue with Email
                </button>
              </div>

              <p className="mt-8 text-center text-xs text-gray-400 leading-relaxed">
                By continuing, you agree to our{" "}
                <a href="https://xplorebyte.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-gray-600">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="https://xplorebyte.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-gray-600">
                  Privacy Policy
                </a>.
              </p>
            </div>

          ) : (
            <>
              {currentStep === 1 && (
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-900 mb-0.5">
                    {isGoogleSignup ? "Complete your profile" : "Your basic details"}
                  </h2>
                  <p className="text-gray-400 text-sm">
                    Step 1 of 3 &nbsp;·&nbsp; 14-day free trial
                  </p>
                </div>
              )}

          <div className="mb-6 flex items-start justify-between">
            {steps.map((step, index) => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center min-w-[90px]">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border transition ${
                        isCompleted
                          ? "bg-emerald-700 border-emerald-700 text-white"
                          : isActive
                            ? "bg-emerald-600 border-emerald-600 text-white ring-2 ring-emerald-200"
                            : "bg-gray-100 border-gray-300 text-gray-500"
                      }`}
                    >
                      {isCompleted ? "✓" : step.id}
                    </div>
                    <span
                      className={`mt-2 text-xs font-medium ${
                        isCompleted
                          ? "text-emerald-700"
                          : isActive
                            ? "text-emerald-700"
                            : "text-gray-400"
                      }`}
                    >
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-px mx-2 mt-4 ${
                        currentStep > step.id ? "bg-emerald-300" : "bg-gray-200"
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {currentStep === 1 && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={profileData.fullName}
                    onChange={handleProfileInputChange}
                    readOnly={isGoogleSignup}
                    disabled={isGoogleSignup}
                    className={`w-full px-3 py-2 border rounded-lg transition text-sm ${
                      isGoogleSignup
                        ? "border-gray-200 bg-gray-100 text-gray-700 cursor-not-allowed"
                        : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    }`}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    readOnly={isGoogleSignup}
                    disabled={isGoogleSignup}
                    className={`w-full px-3 py-2 border rounded-lg transition text-sm ${
                      isGoogleSignup
                        ? "border-gray-200 bg-gray-100 text-gray-700 cursor-not-allowed"
                        : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    }`}
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                {requiresPassword && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm"
                        placeholder="Create a strong password"
                        required={requiresPassword}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                )}


                <div className="pt-4 flex justify-between">
                  {isGoogleSignup ? (
                    <button
                      type="button"
                      onClick={handleGoogleStep1Back}
                      className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                    >
                      Back
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAuthMethod(null)}
                      className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                    >
                      Back
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleStep1Continue}
                    disabled={!isStep1Ready}
                    className={`px-5 py-2 rounded-lg font-medium text-sm ${
                      isStep1Ready
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>
                <div className="px-2 sm:px-6 md:px-10">
                  <div className="max-w-[600px] mx-auto">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number *
                    </label>
                    <div className="flex items-center justify-center gap-2">
                      <select
                        name="countryCode"
                        className="px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm w-[108px]"
                      >
                        <option value="+91">IN +91</option>
                        <option value="+1">US +1</option>
                        <option value="+44">UK +44</option>
                        <option value="+61">AU +61</option>
                      </select>
                      <input
                        type="tel"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                        onChange={handleInputChange}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={10}
                        className="w-full max-w-[330px] px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm"
                        placeholder="Enter 10-digit mobile number"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => sendOtp(formData.mobileNumber)}
                        disabled={
                          otpVerified ||
                          formData.mobileNumber.length !== 10 ||
                          loading ||
                          otpLoading ||
                          resendCooldown > 0
                        }
                        className={`inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap min-w-[112px] ${
                          otpVerified
                            ? "bg-emerald-100 text-emerald-700 border border-emerald-300 cursor-default"
                            : formData.mobileNumber.length === 10 &&
                                !loading &&
                                !otpLoading &&
                                resendCooldown === 0
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                        }`}
                      >
                        <span>
                          {otpVerified
                            ? "Verified"
                            : otpLoading
                              ? "Sending..."
                              : "Send OTP"}
                        </span>
                      </button>
                    </div>

                    <div className="mt-2 flex items-center justify-center gap-2 min-h-[18px]">
                      {resendCooldown > 0 && (
                        <span className="text-xs text-gray-500">
                          Resend in {resendCooldown}s
                        </span>
                      )}
                    </div>

                    {otpSent && !otpVerified && (
                      <div className="mt-3 flex flex-col items-center gap-3">
                        <div className="flex items-center gap-2" onPaste={handleOtpPaste}>
                          {Array.from({ length: 6 }).map((_, idx) => (
                            <input
                              key={idx}
                              ref={el => {
                                otpInputRefs.current[idx] = el;
                              }}
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              maxLength={1}
                              value={otp[idx] || ""}
                              onChange={e => handleOtpDigitChange(idx, e.target.value)}
                              onKeyDown={e => handleOtpKeyDown(idx, e)}
                              className="w-11 h-12 rounded-xl border border-gray-300 bg-white text-center text-lg font-semibold tracking-wide focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                              aria-label={`OTP digit ${idx + 1}`}
                            />
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={handleVerifyOtpAndContinue}
                          disabled={otp.length !== 6 || otpLoading}
                          className={`px-5 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                            otp.length === 6 && !otpLoading
                              ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                              : "bg-gray-200 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          {otpLoading ? "Verifying..." : "Verify OTP"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 flex justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleStep2Continue}
                    disabled={!otpVerified || !verificationToken}
                    className={`px-5 py-2 rounded-lg font-medium text-sm ${
                      otpVerified && verificationToken
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    Next
                  </button>
                </div>
              </>
            )}

            {currentStep === 3 && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={profileData.companyName}
                      onChange={handleProfileInputChange}
                      className="w-full px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm"
                      placeholder="Enter your company name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company Size
                    </label>
                    <select
                      name="companySize"
                      value={profileData.companySize}
                      onChange={handleProfileInputChange}
                      className="w-full px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10</option>
                      <option value="11-50">11-50</option>
                      <option value="51-200">51-200</option>
                      <option value="201-500">201-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Country
                    </label>
                    <Select
                      inputId="signupCountry"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable
                      isSearchable
                      options={countryOptions}
                      value={
                        countryOptions.find(
                          opt => opt.value === profileData.country,
                        ) || null
                      }
                      onChange={opt =>
                        setProfileData(prev => ({
                          ...prev,
                          country: opt?.value || "",
                          state: "",
                        }))
                      }
                      placeholder="Select country..."
                      noOptionsMessage={() => "No country found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      maxMenuHeight={280}
                      styles={signupSelectStyles}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    {isIndiaSelected ? (
                      <>
                        <Select
                          inputId="signupState"
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable
                          isSearchable
                          options={stateOptions}
                          value={
                            stateOptions.find(
                              opt => opt.value === profileData.state,
                            ) || null
                          }
                          onChange={opt =>
                            setProfileData(prev => ({
                              ...prev,
                              state: opt?.value || "",
                            }))
                          }
                          placeholder="Select state / UT..."
                          noOptionsMessage={() => "No state found"}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          maxMenuHeight={280}
                          styles={signupSelectStyles}
                        />
                      </>
                    ) : (
                      <input
                        type="text"
                        value=""
                        disabled
                        className="w-full px-3 h-9 border border-gray-200 rounded-lg bg-gray-50 text-gray-400 text-sm"
                        placeholder="Not required for selected country"
                      />
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <Select
                      inputId="signupIndustry"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isClearable
                      isSearchable
                      options={industryOptions}
                      value={
                        industryOptions.find(
                          opt => opt.value === profileData.industry,
                        ) || null
                      }
                      onChange={opt =>
                        setProfileData(prev => ({
                          ...prev,
                          industry: opt?.value || "",
                        }))
                      }
                      placeholder="Select industry..."
                      noOptionsMessage={() => "No industry found"}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuPlacement="auto"
                      maxMenuHeight={280}
                      styles={signupSelectStyles}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Website
                    </label>
                    <input
                      type="url"
                      name="website"
                      value={profileData.website}
                      onChange={handleProfileInputChange}
                      className="w-full px-3 h-9 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-sm"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div className="flex items-start space-x-3 mt-2">
                  <div className="flex items-center h-5">
                    <input
                      id="accept-terms"
                      name="acceptTerms"
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={e => setAcceptTerms(e.target.checked)}
                      className="w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
                      required
                    />
                  </div>
                  <div className="text-xs leading-5 font-medium">
                    <label
                      htmlFor="accept-terms"
                      className="text-gray-500 font-medium"
                    >
                      I agree to the{" "}
                      <a
                        href="https://xplorebyte.com/terms-of-service"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 hover:underline font-medium"
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://xplorebyte.com/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 hover:underline font-medium"
                      >
                        Privacy Policy.
                      </a>
                    </label>
                  </div>
                </div>

                <div className="pt-4 flex justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium text-sm"
                  >
                    Back
                  </button>

                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    className={`flex-1 py-2 px-5 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center space-x-2 ${
                      isFormValid && !loading
                        ? "bg-gradient-to-r from-emerald-600 to-sapphire-600 hover:from-emerald-700 hover:to-sapphire-700 text-white shadow-md hover:shadow-lg"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creating Account...</span>
                      </>
                    ) : (
                      <>
                        <span>Start Your FREE Trial</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupForTrial;
